import { Asset, AssetVersion, AssetWithVersions } from '../types/asset-types';

/**
 * Normalizes an S3 asset URL by ensuring it has the proper S3 domain prefix.
 * Handles both complete and incomplete URLs safely.
 *
 * @param url - The URL from the API (may be complete or incomplete)
 * @param s3BaseUrl - The S3 base URL (e.g., "https://s3.us-east-1.amazonaws.com")
 * @returns The normalized complete URL
 *
 * @example
 * // Incomplete URL (missing domain)
 * normalizeS3Url('qa.nayax.com/ev/assets/...', 'https://s3.us-east-1.amazonaws.com')
 * // Returns: 'https://s3.us-east-1.amazonaws.com/qa.nayax.com/ev/assets/...'
 *
 * @example
 * // Already complete URL (no changes)
 * normalizeS3Url('https://s3.us-east-1.amazonaws.com/qa.nayax.com/...', 'https://s3.us-east-1.amazonaws.com')
 * // Returns: 'https://s3.us-east-1.amazonaws.com/qa.nayax.com/...' (unchanged)
 */
export function normalizeS3Url(url: string, s3BaseUrl: string): string {
  // Handle edge cases
  if (!url || typeof url !== 'string') {
    return url;
  }

  // If no S3 base URL configured (e.g., production with empty string), return as-is
  if (!s3BaseUrl || s3BaseUrl.trim() === '') {
    return url;
  }

  // Check if URL is already a valid asset URL
  // Valid URLs: S3 URLs or production CDN URLs
  if (url.startsWith('https://s3.') ||
      url.startsWith('https://s3-') ||
      url.startsWith('http://s3.') ||
      url.startsWith('https://static.nayax.com')) {
    return url;
  }

  // If URL has protocol but is NOT a valid asset URL (e.g., https://qa.nayax.com/ev/assets/...)
  // Extract the hostname and path, then prepend S3 base URL
  if (url.startsWith('https://') || url.startsWith('http://')) {
    try {
      const urlObj = new URL(url);
      // Include both hostname (bucket) and path
      // Example: https://qa.nayax.com/ev/assets/... -> qa.nayax.com/ev/assets/...
      const bucketAndPath = urlObj.hostname + urlObj.pathname;
      const normalizedBase = s3BaseUrl.endsWith('/') ? s3BaseUrl.slice(0, -1) : s3BaseUrl;
      return `${normalizedBase}/${bucketAndPath}`;
    } catch (e) {
      // If URL parsing fails, fall through to simple prepending
      console.warn('Failed to parse URL:', url, e);
    }
  }

  // URL is incomplete (no protocol) - prepend S3 base URL
  const normalizedBase = s3BaseUrl.endsWith('/') ? s3BaseUrl.slice(0, -1) : s3BaseUrl;
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;

  return `${normalizedBase}${normalizedPath}`;
}

/**
 * Transforms an Asset object by normalizing its S3 URL.
 * Creates a new object (immutable pattern).
 *
 * @param asset - The asset from the API
 * @param s3BaseUrl - The S3 base URL
 * @returns New asset object with normalized currentS3Url
 */
export function transformAssetUrls(asset: Asset, s3BaseUrl: string): Asset {
  return {
    ...asset,
    currentS3Url: normalizeS3Url(asset.currentS3Url, s3BaseUrl),
  };
}

/**
 * Transforms an AssetWithVersions object by normalizing all S3 URLs.
 * Handles both current URL and all version URLs.
 *
 * @param assetWithVersions - The asset with version history from the API
 * @param s3BaseUrl - The S3 base URL
 * @returns New object with all URLs normalized
 */
export function transformAssetWithVersionUrls(
  assetWithVersions: AssetWithVersions,
  s3BaseUrl: string
): AssetWithVersions {
  return {
    ...assetWithVersions,
    currentS3Url: normalizeS3Url(assetWithVersions.currentS3Url, s3BaseUrl),
    versions: assetWithVersions.versions.map((version) =>
      transformVersionUrls(version, s3BaseUrl)
    ),
  };
}

/**
 * Transforms an AssetVersion object by normalizing its S3 URL.
 *
 * @param version - The version from the API
 * @param s3BaseUrl - The S3 base URL
 * @returns New version object with normalized s3Url
 */
export function transformVersionUrls(version: AssetVersion, s3BaseUrl: string): AssetVersion {
  return {
    ...version,
    s3Url: normalizeS3Url(version.s3Url, s3BaseUrl),
  };
}
